import React, { useCallback, useEffect, useMemo, useState } from "react";

import { faServer } from "@fortawesome/pro-regular-svg-icons";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";

import { ModuleKindSection } from "src/adminPanel/modulesV2/ModuleKindSection";
import {
  enrichModulesForSearch,
  getModuleKindDisplayName,
  MODULE_SEARCH_OPTIONS,
} from "src/adminPanel/modulesV2/utils";
import { useAllOrgs } from "src/adminPanel/queries";
import { useModulesByKind } from "src/api/modulesApi";
import { useWorkspaces } from "src/api/queries";
import { Input } from "src/base-components/Input";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Tabs } from "src/base-components/Tabs";
import { ModuleKind } from "src/clients/flow-api";
import { EmptyState } from "src/design-system/EmptyState";
import { useFuseSearch } from "src/utils/useFuseSearch";

const allKinds: ModuleKind[] = Object.values(ModuleKind);

export const ModulesV2Settings: React.FC = () => {
  const {
    byKind: grouped,
    allModules,
    isLoading,
    isError,
  } = useModulesByKind();
  const { data: workspaces } = useWorkspaces(true, true);
  const { data: organizations } = useAllOrgs();
  const [selectedKindTab, setSelectedKindTab] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();

  const workspaceMap = useMemo(
    () =>
      new Map(
        workspaces?.map((ws) => [
          ws.id,
          { name: ws.name, orgId: ws.organization_id, slug: ws.slug },
        ]),
      ),
    [workspaces],
  );
  const orgMap = useMemo(
    () => new Map(organizations?.map((org) => [org.id, org.name])),
    [organizations],
  );

  const searchTerm = searchParams.get("search") || "";
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchParams(
      (prev) => {
        if (newValue) {
          prev.set("search", newValue);
        } else {
          prev.delete("search");
        }
        return prev;
      },
      { replace: true },
    );
  };

  const handleTabChange = useCallback(
    (tabKey: string) => {
      setSelectedKindTab(tabKey);
      setSearchParams(
        (prev) => {
          prev.set("kind", tabKey);
          return prev;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    const urlType = searchParams.get("kind") || "";

    if (
      urlType &&
      allKinds.includes(urlType as ModuleKind) &&
      urlType !== selectedKindTab
    ) {
      setSelectedKindTab(urlType);
      return;
    }

    if (!selectedKindTab) {
      const defaultKind = allKinds[0];
      setSelectedKindTab(defaultKind);
      if (urlType !== defaultKind) {
        setSearchParams(
          (prev) => {
            prev.set("kind", defaultKind);
            return prev;
          },
          { replace: true },
        );
      }
    }
  }, [searchParams, selectedKindTab, setSearchParams]);

  const agentWorkspaceIds = useMemo(
    () =>
      new Set(
        allModules
          .filter((m) => m.kind === ModuleKind.AGENTS)
          .map((m) => m.workspaceId),
      ),
    [allModules],
  );

  const allSearchableModules = useMemo(
    () => enrichModulesForSearch(allModules, workspaceMap, orgMap),
    [allModules, workspaceMap, orgMap],
  );
  const fuseSearch = useFuseSearch(allSearchableModules, MODULE_SEARCH_OPTIONS);
  const searchResults = useMemo(
    () => fuseSearch(debouncedSearchTerm),
    [fuseSearch, debouncedSearchTerm],
  );
  const filteredCounts = useMemo(
    () =>
      Object.fromEntries(
        allKinds.map((kind) => [
          kind,
          searchResults.filter((mod) => mod.kind === kind).length,
        ]),
      ),
    [searchResults],
  );

  useEffect(() => {
    if (!debouncedSearchTerm) return;
    if (filteredCounts[selectedKindTab] > 0) return;
    const firstWithResults = allKinds.find((kind) => filteredCounts[kind] > 0);
    if (firstWithResults) {
      handleTabChange(firstWithResults);
    }
  }, [debouncedSearchTerm, filteredCounts, selectedKindTab, handleTabChange]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-screen-2xl">
          <div className="mb-8 space-y-2">
            <SkeletonPlaceholder height="h-[32px]" width="w-[300px]" />
            <SkeletonPlaceholder height="h-[20px]" width="w-[400px]" />
          </div>
          <SkeletonPlaceholder height="h-[40px]" width="w-full" />
          <div className="mt-6 space-y-3">
            <SkeletonPlaceholder height="h-[48px]" width="w-full" />
            <SkeletonPlaceholder height="h-[48px]" width="w-full" />
            <SkeletonPlaceholder height="h-[48px]" width="w-full" />
            <SkeletonPlaceholder height="h-[48px]" width="w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="mx-auto max-w-screen-2xl">
          <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
            <EmptyState
              description="Failed to load modules. Please try again."
              headline="Error loading modules"
              icon={faServer}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-screen-2xl">
        <div className="mb-8">
          <h1 className="font-inter-semibold-32px text-gray-900">Modules</h1>
          <p className="mt-2 font-inter-normal-16px text-gray-600">
            Manage and monitor modules with operation tracking.
          </p>
        </div>

        <div className="mb-6">
          <Input
            placeholder="Search by organization, workspace, module ID or active operation ID..."
            type="text"
            value={searchTerm}
            fullWidth
            onChange={handleSearchChange}
          />
        </div>

        <Tabs
          containerClassName="bg-white rounded-xl border border-gray-200 shadow-sm"
          panelsClassName="p-6"
          selectedKey={selectedKindTab || allKinds[0]}
          tabClassName="border-b border-gray-200 px-6"
          tabs={allKinds.map((kind) => ({
            key: kind,
            label: (
              <div className="flex items-center gap-2">
                <span>{getModuleKindDisplayName(kind)}</span>
                <span className="rounded-full bg-gray-100 px-2 py-0.5 font-inter-normal-12px text-gray-500">
                  {debouncedSearchTerm
                    ? `${filteredCounts[kind]}/${grouped[kind].length}`
                    : grouped[kind].length}
                </span>
              </div>
            ),
            content: (
              <ModuleKindSection
                agentWorkspaceIds={agentWorkspaceIds}
                moduleKind={kind}
                modules={grouped[kind]}
                orgMap={orgMap}
                searchTerm={debouncedSearchTerm}
                workspaceMap={workspaceMap}
                workspaces={workspaces ?? []}
              />
            ),
          }))}
          onChange={handleTabChange}
        />
      </div>
    </div>
  );
};
